import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DragDropContext } from 'react-beautiful-dnd';
import {
  IlogisticServiceTrelloCardData,
  ITrello,
  LOCATION_PLACE_TYPE,
  LOGISTIC_DROPPABLE_ID,
  LOGISTIC_SERVICE_STATUS_NAME,
  TlogisticsMovementsTypes
} from '../../../../types/services';
import LogisticsServicesTrelloModals from '../ModalsLogisticsService/LogisticsServicesTrelloModals';
import errorMessage from '../../../../utils/errorMessage';
import { displayErrorNotification } from '../../../../utils/displayNudosStandardNotifications';
import LogisticServiceSpaceCard from '../LogisticServiceSpaceCard/LogisticServiceSpaceCard';
import LogisticServiceTrelloColumn from '../LogisticServiceTrelloColumn/LogisticServiceTrelloColumn';
import useLsTrelloState from '../../../../state/useLsTrelloState';
import './LogisticsServicesTrello.scss';

const LogisticsServicesTrello = ({
  searchText,
  changeStateFirsLoad
}: {
  searchText: string;
  changeStateFirsLoad: (state: boolean) => void;
}) => {
  const { push } = useHistory();
  const { logisticServices, setLogisticServices, initialLogisticServices, setDragEndData } = useLsTrelloState();

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showDelayedModal, setShowDelayedModal] = useState<boolean>(false);
  const [showActiveModal, setShowActiveModal] = useState<boolean>(false);
  const [showDeliveredModal, setShowDeliveredModal] = useState<boolean>(false);
  const [showInProcessModal, setShowInProcessModal] = useState<boolean>(false);
  const [selectedLogisticServiceDataForModal, setSelectedLogisticServiceDataForModal] =
    useState<IlogisticServiceTrelloCardData>();

  const { CANCELLED, CONFIRMED, ACTIVE, DELIVERED, INPROCESS, DELAYED, TOCONFIRMED } = LOGISTIC_SERVICE_STATUS_NAME;
  const { CANCELLED_ID, CONFIRMED_ID, ACTIVE_ID, DELIVERED_ID, INPROCESS_ID, DELAYED_ID, TOCONFIRMED_ID } =
    LOGISTIC_DROPPABLE_ID;

  const modifyLocalLists = (
    item: IlogisticServiceTrelloCardData,
    origin: TlogisticsMovementsTypes,
    destination: TlogisticsMovementsTypes
  ) => {
    const originList = logisticServices?.[origin]?.filter(originElement => originElement.id !== item.id);
    const oldDestinationList = logisticServices?.[destination] || [];
    const newDestinationList = [item, ...oldDestinationList];
    setLogisticServices({ ...logisticServices, [origin]: originList, [destination]: newDestinationList });
  };

  const handleChangeCards = (result: ITrello) => {
    const draggableId = Number(result?.draggableId);
    const pendingItem = logisticServices?.pendings?.find(pendingElement => pendingElement.id === draggableId);
    const confirmItem = logisticServices?.confirmed?.find(confirmElement => confirmElement.id === draggableId);
    const inProcessItem = logisticServices?.inProcess?.find(inProcessElement => inProcessElement.id === draggableId);
    const activeItem = logisticServices?.actives?.find(activeElement => activeElement.id === draggableId);
    const delayedItem = logisticServices?.delayed?.find(delayedElement => delayedElement.id === draggableId);
    const originIsPending = result.source?.droppableId === TOCONFIRMED_ID;
    const originIsConfirmed = result?.source?.droppableId === CONFIRMED_ID;
    const originIsInProcess = result?.source?.droppableId === INPROCESS_ID;
    const originIsActive = result.source?.droppableId === ACTIVE_ID;
    const originIsDelayed = result?.source?.droppableId === DELAYED_ID;
    const destinationIsConfirm = result.destination?.droppableId === CONFIRMED_ID;
    const destinationIsInProcess = result.destination?.droppableId === INPROCESS_ID;
    const destinationIsActive = result.destination?.droppableId === ACTIVE_ID;
    const destinationIsCanceled = result.destination?.droppableId === CANCELLED_ID;
    const destinationIsDelivered = result.destination?.droppableId === DELIVERED_ID;
    const notDelivered = originIsPending || originIsConfirmed || originIsActive || originIsDelayed || originIsInProcess;
    const originViewedChanges = pendingItem?.addressIssue?.origin?.viewedChanges;
    const destinationViewedChanges = pendingItem?.addressIssue?.destination?.viewedChanges;
    const originAddressEditedByUser = pendingItem?.addressIssue?.origin?.editedBy === 'user';
    const destinationAddressEditedByUser = pendingItem?.addressIssue?.destination?.editedBy === 'user';
    const originAddressEditedByUserAndNotViewed = originAddressEditedByUser && !originViewedChanges;
    const destinationAddressEditedByUserAndNotViewed = destinationAddressEditedByUser && !destinationViewedChanges;
    if (notDelivered && destinationIsCanceled && !!draggableId) {
      setSelectedLogisticServiceDataForModal(pendingItem || confirmItem || activeItem || delayedItem || inProcessItem);
      if (pendingItem) {
        // pendiente a cancelado
        modifyLocalLists(pendingItem, 'pendings', 'cancelled');
      }
      if (confirmItem) {
        //confirmado a cancelado
        modifyLocalLists(confirmItem, 'confirmed', 'cancelled');
      }
      if (inProcessItem) {
        //procesado a cancelado
        modifyLocalLists(inProcessItem, 'inProcess', 'cancelled');
      }
      if (activeItem) {
        //activo a cancelado
        modifyLocalLists(activeItem, 'actives', 'cancelled');
      }
      if (delayedItem) {
        //atrasado a cancelado
        modifyLocalLists(delayedItem, 'delayed', 'cancelled');
      }
      setShowCancelModal(true);
    }
    if (originIsPending && destinationIsConfirm && pendingItem && !!draggableId) {
      //pendiente a confirmado
      if (originAddressEditedByUserAndNotViewed || destinationAddressEditedByUserAndNotViewed)
        return displayErrorNotification({
          customJSXMessage: <>No puedes mover el servicio hasta revisar los cambios realizados por el usuario</>
        });
      if (!pendingItem?.quoteDocumentUrl)
        return errorMessage('Se necesita una cotización para mover el servicio logístico a confirmado');
      modifyLocalLists(pendingItem, 'pendings', 'confirmed');
      setSelectedLogisticServiceDataForModal(pendingItem);
      setShowConfirmModal(true);
    }
    if (originIsConfirmed && destinationIsInProcess && !!draggableId && confirmItem) {
      // confirmado a proceso
      const originViewedChanges = confirmItem?.addressIssue?.origin?.viewedChanges;
      const destinationViewedChanges = confirmItem?.addressIssue?.destination?.viewedChanges;
      const originAddressEdited = confirmItem?.addressIssue?.origin?.editedBy === 'user';
      const destinationAddressEdited = confirmItem?.addressIssue?.destination?.editedBy === 'user';
      if ((!originViewedChanges && !!originAddressEdited) || (!destinationViewedChanges && !!destinationAddressEdited))
        return displayErrorNotification({
          customJSXMessage: <>No puedes mover el servicio hasta revisar los cambios realizados por el usuario</>
        });
      modifyLocalLists(confirmItem, 'confirmed', 'inProcess');
      setSelectedLogisticServiceDataForModal(confirmItem);
      setShowInProcessModal(true);
    }
    if (originIsInProcess && destinationIsActive && !!draggableId && inProcessItem) {
      //proceso a activo
      modifyLocalLists(inProcessItem, 'inProcess', 'actives');
      setSelectedLogisticServiceDataForModal(inProcessItem);
      setShowActiveModal(true);
    }
    if ((originIsActive || originIsDelayed) && destinationIsDelivered) {
      const { id, hasPendingConfiguration, origin, destination } = activeItem || delayedItem || {};
      const originIsWarehouse = origin?.place === LOCATION_PLACE_TYPE.NUDOS;
      const destinationIsNotWarehouse = destination?.place !== LOCATION_PLACE_TYPE.NUDOS;
      const cantMoveDueToPendingConfigurations =
        hasPendingConfiguration && originIsWarehouse && destinationIsNotWarehouse;
      if (cantMoveDueToPendingConfigurations) {
        return displayErrorNotification({
          customJSXMessage: <>No puedes mover el servicio a entregado hasta completar las configuraciones</>,
          button1Text: 'Ir a las configuraciones',
          button1Action: () => push(`/logistic-service/details/${id}`, { initialTab: 2 })
        });
      }
      setSelectedLogisticServiceDataForModal(activeItem || delayedItem);
      if (activeItem) {
        //activo a entregado
        modifyLocalLists(activeItem, 'actives', 'delivered');
      }
      if (delayedItem) {
        // atrasado a entregado
        modifyLocalLists(delayedItem, 'delayed', 'delivered');
      }
      setShowDeliveredModal(true);
    }
  };

  const initialData = () => {
    if (initialLogisticServices) setLogisticServices(initialLogisticServices);
  };

  const closeAndRestoreInitialList = () => {
    setShowActiveModal(false);
    setShowDelayedModal(false);
    setShowDeliveredModal(false);
    setShowConfirmModal(false);
    setShowCancelModal(false);
    setShowInProcessModal(false);
    setSelectedLogisticServiceDataForModal(undefined);
    initialData();
  };

  const openDelayedModalCallback = (logisticServiceTrelloData: IlogisticServiceTrelloCardData) => {
    setSelectedLogisticServiceDataForModal(logisticServiceTrelloData);
    setShowDelayedModal(true);
  };

  return (
    <>
      <LogisticsServicesTrelloModals
        selectedLogisticServiceDataForModal={selectedLogisticServiceDataForModal}
        setSelectedLogisticServiceDataForModal={setSelectedLogisticServiceDataForModal}
        closeAndRestoreList={closeAndRestoreInitialList}
        cancelModalData={{ showModal: showCancelModal, closeModal: () => setShowCancelModal(false) }}
        confirmModalData={{ showModal: showConfirmModal, closeModal: () => setShowConfirmModal(false) }}
        processModalData={{ showModal: showInProcessModal, closeModal: () => setShowInProcessModal(false) }}
        delayedModalData={{ showModal: showDelayedModal, closeModal: () => setShowDelayedModal(false) }}
        deliveredModalData={{ showModal: showDeliveredModal, closeModal: () => setShowDeliveredModal(false) }}
        activeModalData={{ showModal: showActiveModal, closeModal: () => setShowActiveModal(false) }}
      />
      <DragDropContext
        onDragEnd={result => {
          setDragEndData(result);
          handleChangeCards(result);
        }}
      >
        <div id="logisticsServicesTrello">
          <div className="logisticsGeneralContainer">
            <div className="activeLogisticsServices">
              <LogisticServiceTrelloColumn
                status={TOCONFIRMED}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
              <LogisticServiceSpaceCard spaceName="Manual" />
              <LogisticServiceTrelloColumn
                status={CONFIRMED}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
              <LogisticServiceSpaceCard spaceName="Manual" />
              <LogisticServiceTrelloColumn
                status={INPROCESS}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
              <LogisticServiceSpaceCard spaceName="Manual" />
              <LogisticServiceTrelloColumn
                status={ACTIVE}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
              <LogisticServiceSpaceCard spaceName="Automatico" />
              <LogisticServiceTrelloColumn
                status={DELAYED}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
                openDelayedModalCallback={openDelayedModalCallback}
              />
              <LogisticServiceSpaceCard spaceName="Manual" />
              <LogisticServiceTrelloColumn
                status={DELIVERED}
                searchText={searchText}
                changeStateFirsLoad={changeStateFirsLoad}
              />
            </div>
            <LogisticServiceTrelloColumn
              status={CANCELLED}
              searchText={searchText}
              changeStateFirsLoad={changeStateFirsLoad}
            />
          </div>
        </div>
      </DragDropContext>
    </>
  );
};
export default LogisticsServicesTrello;
